import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import HeadphonesOutlinedIcon from "@mui/icons-material/HeadphonesOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const SECTIONS = [
    {
        key: "listening",
        icon: HeadphonesOutlinedIcon,
        title: "Listening",
        description:
            "Practice with authentic audio recordings covering all question types. Improve your ability to catch details, follow conversations, and manage time effectively.",
        features: [
            "4 sections, 40 questions",
            "Various accents (British, American, Australian)",
            "All question types included",
            "Instant AI feedback on weak areas",
        ],
        buttonLabel: "Practice Listening",
    },
    {
        key: "reading",
        icon: AutoStoriesOutlinedIcon,
        title: "Reading",
        description:
            "Master reading passages with timed practice. Learn to locate information quickly, understand complex arguments, and answer efficiently under pressure.",
        features: [
            "Academic & General Training versions",
            "3 passages, 40 questions",
            "Time management training",
            "Detailed answer explanations",
        ],
        buttonLabel: "Practice Reading",
    },
    {
        key: "writing",
        icon: CreateOutlinedIcon,
        title: "Writing",
        description:
            "Get AI-powered feedback on Task 1 and Task 2. Understand exactly how your response maps to band descriptors and what specific improvements to make.",
        features: [
            "Task 1 & Task 2 practice",
            "AI scoring on all 4 criteria",
            "Vocabulary & grammar suggestions",
            "Band-specific improvement tips",
        ],
        buttonLabel: "Practice Writing",
    },
    {
        key: "speaking",
        icon: MicOutlinedIcon,
        title: "Speaking",
        description:
            "Practice all three parts of the Speaking test with our AI evaluator. Get feedback on fluency, pronunciation, vocabulary range, and coherence.",
        features: [
            "All 3 parts covered",
            "AI pronunciation analysis",
            "Fluency & coherence feedback",
            "Practice anytime, anywhere",
        ],
        buttonLabel: "Practice Speaking",
    },
];

function SectionCard({ section, onClick }) {
    const Icon = section.icon;

    return (
        <Box
            onClick={() => onClick(section.key)}
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                padding: "28px 24px",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
            }}
        >
            {/* Icon container */}
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#DBEAFE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: "16px",
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ color: "#3B82F6", fontSize: "1.4rem" }} />
            </Box>

            {/* Title */}
            <Typography
                sx={{
                    color: "#111827",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 700,
                    fontSize: "1.15rem",
                    mb: "10px",
                }}
            >
                {section.title}
            </Typography>

            {/* Description */}
            <Typography
                sx={{
                    color: "#6B7280",
                    fontFamily: "Mulish, sans-serif",
                    fontSize: "0.875rem",
                    lineHeight: 1.65,
                    mb: "20px",
                }}
            >
                {section.description}
            </Typography>

            {/* Feature list */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mb: "28px", flexGrow: 1 }}>
                {section.features.map((feature) => (
                    <Box
                        key={feature}
                        sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                        <CheckCircleOutlineIcon
                            sx={{ color: "#3B9EFF", fontSize: "1.05rem", flexShrink: 0 }}
                        />
                        <Typography
                            sx={{
                                color: "#374151",
                                fontFamily: "Mulish, sans-serif",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                            }}
                        >
                            {feature}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* CTA Button */}
            <Box>
                <Button
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); onClick(section.key); }}
                    endIcon={<span style={{ fontSize: "1rem" }}>→</span>}
                    sx={{
                        border: "1px solid #D1D5DB",
                        color: "#374151",
                        borderRadius: "6px",
                        padding: "8px 18px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        fontFamily: "Mulish, sans-serif",
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: "#F9FAFB",
                            border: "1px solid #9CA3AF",
                        },
                    }}
                >
                    {section.buttonLabel}
                </Button>
            </Box>
        </Box>
    );
}

export default function PracticeBySection({ onSectionSelect }) {
    const handleClick = (sectionKey) => {
        if (onSectionSelect) onSectionSelect(sectionKey);
    };

    return (
        <Box
            sx={{
                backgroundColor: "#EEF2F7",
                width: "100%",
                px: "76px",
                py: "64px",
            }}
        >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: "48px" }}>
                <Typography
                    sx={{
                        color: "#111827",
                        fontFamily: "Mulish, sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "1.75rem", md: "2.25rem" },
                        mb: "12px",
                    }}
                >
                    Practice by Section
                </Typography>
                <Typography
                    sx={{
                        color: "#6B7280",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "0.975rem",
                        lineHeight: 1.6,
                    }}
                >
                    Master each component of the IELTS exam with targeted practice and feedback.
                </Typography>
            </Box>

            {/* 2×2 Card Grid */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: "24px",
                }}
            >
                {SECTIONS.map((section) => (
                    <SectionCard
                        key={section.key}
                        section={section}
                        onClick={handleClick}
                    />
                ))}
            </Box>
        </Box>
    );
}
